import { useEffect, useState } from 'react';
import { Card, CardBody, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { JobTypeService } from './service';
import type { JobType } from './interface';
import { ErrorToast, SuccessToast } from '../../shared/toasters/toasters';
import DeleteModal from '../../Common/DeleteModal';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import Pagination from '../../Common/Pagination';

const ENTRIES_PER_PAGE_OPTIONS = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '20', value: 20 },
  { label: '30', value: 30 },
  { label: '40', value: 40 },
];

const JobTypeList = () => {
  const { t } = useTranslation();
  const [list, setList] = useState<JobType[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [term, setTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobTypeToDelete, setJobTypeToDelete] = useState<number | null>(null);
  const navigate = useNavigate();

  const handleTermChange = (e: any) => {
    setTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleEntriesPerPageChange = (selectedOption: any) => {
    setEntriesPerPage(selectedOption ? selectedOption.value : 10);
    setCurrentPage(1);
  };

  const handleDeleteClick = (id: number) => {
    setJobTypeToDelete(id);
    setShowDeleteModal(true);
  };

  const fetchData = async () => {
    try {
      const response = await JobTypeService.getAll({
        page: currentPage,
        perPage: entriesPerPage,
        term,
      });
      setList(response.data);
      setTotalPages(response.totalPages);
    } catch (error) {
      ErrorToast(error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (jobTypeToDelete !== null) {
      try {
        await JobTypeService.delete(jobTypeToDelete);
        SuccessToast(t('jobType.successDelete'));
        setShowDeleteModal(false);
        setJobTypeToDelete(null);
        fetchData();
      } catch (error) {
        ErrorToast(error);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, entriesPerPage, term]);

  return (
    <>
      <Col>
        <Card>
          <CardBody className="card-body">
            <div className='text-end'>
              <button className='btn btn-primary text-end' onClick={() => navigate('/job-types/create')}><i className='ti ti-plus f-18'></i> {t('jobType.add')}</button>
            </div>

            <div className="d-sm-flex align-items-center mb-3">
              <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                  <Select
                    placeholder={t('jobType.entriesPerPage')}
                    options={ENTRIES_PER_PAGE_OPTIONS}
                    onChange={handleEntriesPerPageChange}
                    defaultValue={ENTRIES_PER_PAGE_OPTIONS.find(option => option.value === entriesPerPage)}
                    classNamePrefix="react-select"
                  />
                </li>
                <span> {t('jobType.entriesPerPage')}</span>
              </ul>
              <ul className="list-inline ms-auto my-1 mt-3">
                <li className="list-inline-item">
                  <input
                    type="search"
                    className="form-control"
                    placeholder={t('jobType.searchPlaceholder')}
                    onChange={handleTermChange}
                  />
                </li>
              </ul>
            </div>
            <div className="table-responsive">
              <table className="table table-hover sticky-actions" id="pc-dt-simple-1">
                <thead>
                  <tr>
                    <th>{t('jobType.id')}</th>
                    <th>{t('jobType.title')}</th>
                    <th>{t('jobType.createdAt')}</th>
                    <th>{t('jobType.updatedAt')}</th>
                    <th className="text-end sticky-col">{t('jobType.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((item: JobType) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td className="wrap-col">{item.title}</td>
                      <td>{new Date(item.createdAt).toLocaleDateString()}</td>
                      <td>{new Date(item.updatedAt).toLocaleDateString()}</td>
                      <td className="text-end text-nowrap sticky-col">
                        <ul className="list-inline mb-0">
                          <li className="list-inline-item">
                            <span
                              role="button"
                              className="avtar avtar-s btn-link-success btn-pc-default"
                              onClick={() => navigate(`/job-types/edit/${item.id}`)}
                            >
                              <i className="ti ti-edit f-20"></i>
                            </span>
                          </li>
                          <li className="list-inline-item">
                            <span
                              role="button"
                              className="avtar avtar-s btn-link-danger btn-pc-default"
                              onClick={() => handleDeleteClick(item.id)}
                            >
                              <i className="ti ti-trash f-20"></i>
                            </span>
                          </li>
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              previousLabel={t('jobType.previous')}
              nextLabel={t('jobType.next')}
            />
          </CardBody>
        </Card>
      </Col>

      <DeleteModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDeleteId={handleDeleteConfirm}
        name={t('jobType.name')}
      />
    </>
  );
};

export default JobTypeList;